import { useEffect, useState } from 'react';
import Head from 'next/head';
import LayoutPage from '@components/LayoutPage';
import { GetServerSideProps } from 'next';
import type { NextPage } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { Exercise } from '@playground/models/exercise.model';
import { getExercise } from '@fetchers/exercise';
import Playground from '@playground/Sandpack';
import { getExerciseFromLocal } from '@services/challenge';
import BackButton from '@components/BackButton';
import NextButton from '@components/NextButton';

interface Props {
  exercise: Exercise;
  embed: boolean;
}

const PlaygroundPage: NextPage<Props> = ({ exercise, embed }) => {
  const { t } = useTranslation('common');
  const [localExercise, setLocalExercise] = useState<Exercise>(null);

  useEffect(() => {
    const localExercise = getExerciseFromLocal(exercise.id);
    if (localExercise) {
      setLocalExercise({
        ...exercise,
        files: localExercise.files,
      });
    } else {
      setLocalExercise(exercise);
    }
  }, [exercise]);

  if (!localExercise) {
    return <p>{t('codeRunner.loading')}</p>;
  }

  if (embed) {
    return (
      <div className="md:h-screen w-full p-4">
        <Playground exercise={exercise} mode="embed" />
      </div>
    );
  }
  return (
    <>
      <Head>
        <title>{exercise.title} | Platzi Hero</title>
        <meta
          name="description"
          content="Pon a prueba tus conocimientos con desafíos interactivos de código basados en conceptos de JavaScript"
        />
      </Head>
      <LayoutPage>
        <div className="flex justify-between">
          <BackButton
            link={`/langs/${exercise.path.langId}/paths/${exercise.path.id}`}
            text={t('codeRunner.back')}
          />
          {localExercise.nextExerciseId && (
            <NextButton link={`/e/${localExercise.nextExerciseId}?path=${exercise.path.id}`} />
          )}
        </div>
        <div className="md:h-[32rem] w-full">
          <Playground exercise={localExercise} mode="external" />
        </div>
      </LayoutPage>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ params, locale, res, query }) => {
  const { id } = params;
  let embed = false;
  if (typeof query['embed'] === 'string') {
    embed = query['embed'] === 'true';
  }
  let path = '';
  if (typeof query['path'] === 'string') {
    path = query['path'];
  }
  if (!path) {
    embed = true;
  }
  if (typeof query['locale'] === 'string') {
    locale = query['locale'];
  }
  if (typeof id === 'string') {
    const exercise = await getExercise('js', id, embed, path, locale);
    if (exercise) {
      const ssrConfig = await serverSideTranslations(locale, ['common']);
      res.setHeader('Cache-Control', 'public, s-maxage=10, stale-while-revalidate=59');
      return {
        props: {
          exercise,
          embed,
          ...ssrConfig,
        },
      };
    }
  }
  return {
    notFound: true,
  };
};

export default PlaygroundPage;
